/**
 * PATH: packages/email-hub/src/feature/components/sender-identity/useSenderIdentityActions.ts
 * FILE_ID: EMAILHUB-SENDERIDENTITY-ACTIONS-001
 * PURPOSE: Own every sender-identity mutation plus the optimistic overlays the
 *          badge states read while the parent refetch is in flight.
 * ROLE IN ARCHITECTURE: State hook behind the SenderIdentityCard frame.
 * MAIN EXPORTS: useSenderIdentityActions, SenderIdentityActions, SENDER_ROLES,
 *   SenderRoleKey, CreateProfileResponse, CreateSupplierResponse.
 * NON-RESPONSIBILITIES: No rendering, no predicate decisions (predicates.ts).
 *
 * NOTES FOR FUTURE AI: The optimistic overlays are load-bearing, not polish.
 * Every mutation here also calls the host's refetch, and the server is the
 * source of truth — the overlay only bridges the gap so the badge flips on the
 * click instead of a round-trip later. One shared ``busy`` lock guards all four
 * mutations; ``pendingKind`` exists only so a spinner lands on the button the
 * operator actually pressed. Moved verbatim out of SenderIdentityCard.tsx
 * (change #302, size split).
 */
import { useState } from 'react'

import { quotechJson } from '../../../_internal/services/quotechApi'
import type { VendorKind } from './predicates'

export interface CreateProfileResponse {
  created: boolean
  contact_id: number | null
  contact_name: string | null
  client_id: number | null
  client_name: string | null
}

export interface CreateSupplierResponse {
  created: boolean
  supplier_id: string | null
  supplier_name: string | null
  contact_email: string | null
  /** 'subscription' when filed as a recurring vendor, else 'supplier'.
   *  Echoed by CreateSenderSupplierView so the optimistic overlay can render
   *  the correct (Subscription vs Supplier) saved badge before the refetch. */
  kind?: VendorKind
}

/** Relationship options for the Teammate chip's role picker — mirrors
 *  backend SENDER_ROLE_CHOICES (organisation.email_sender_teammate).
 *  ``internal`` decides whether the address keeps teammate treatment or
 *  returns to the external-sender flow (CRM, supplier nudges). */
export const SENDER_ROLES = {
  employee: { label: 'Employee', internal: true },
  contractor: { label: 'Contractor', internal: true },
  external_contractor: { label: 'External contractor', internal: false },
  client: { label: 'Client', internal: false },
  supplier: { label: 'Supplier', internal: false },
} as const

export type SenderRoleKey = keyof typeof SENDER_ROLES

export interface SenderIdentityActions {
  busy: boolean
  error: string | null
  /** Which vendor button is in flight, so the spinner lands on the one the
   *  operator clicked (Supplier and Subscription share one request lock). */
  pendingKind: VendorKind | null
  created: CreateProfileResponse | null
  supplierSaved: CreateSupplierResponse | null
  detachedWrongPerson: boolean
  teammateOverridden: boolean
  chosenRole: SenderRoleKey | null
  roleMenuOpen: boolean
  setRoleMenuOpen: (open: boolean) => void
  addToCrm: () => void
  addVendor: (kind: VendorKind) => void
  detachWrongPerson: () => void
  overrideTeammate: (
    action: 'not_teammate' | 'set_role' | 'undo',
    role?: SenderRoleKey,
  ) => void
}

/**
 * All four sender-identity mutations plus their optimistic state.
 *
 * Inputs: the email id and the host's post-write refetch.
 * Output: flags the badge states render from, and void-returning kicks.
 * Failure modes: every mutation surfaces its message on ``error`` and leaves
 * the previous badge intact — a failed write never fakes a saved state.
 */
export function useSenderIdentityActions(
  emailId: string,
  onProfileCreated?: () => void,
): SenderIdentityActions {
  // Optimistic local overlay so the strip flips to "known" the instant
  // the create succeeds, even before the parent's refetch lands. Null
  // until we create something; the email prop is the source of truth
  // once the detail refetch returns.
  const [created, setCreated] = useState<CreateProfileResponse | null>(null)
  const [supplierSaved, setSupplierSaved] = useState<CreateSupplierResponse | null>(null)
  // "Wrong person? Fix" — optimistic flag once the mis-filed contact's
  // claim on this address is severed server-side, so the badge drops
  // immediately (the parent refetch then confirms from fresh data).
  const [detachedWrongPerson, setDetachedWrongPerson] = useState(false)
  // "Not a teammate?" — optimistic flag once the sender is pinned out of
  // teammate resolution server-side, so the chip flips immediately.
  const [teammateOverridden, setTeammateOverridden] = useState(false)
  // Relationship picker (operator 2026-07-09: "she could be employee,
  // contractor or whatever… ability to change is critical").
  const [roleMenuOpen, setRoleMenuOpen] = useState(false)
  const [chosenRole, setChosenRole] = useState<SenderRoleKey | null>(null)
  const [busy, setBusy] = useState(false)
  const [pendingKind, setPendingKind] = useState<VendorKind | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Pin/unpin/reassign this sender's relationship (the chip's role
  // picker). Server is the source of truth; the optimistic flags only
  // bridge until the parent refetch lands.
  async function handleTeammateOverride(
    action: 'not_teammate' | 'set_role' | 'undo',
    role?: SenderRoleKey,
  ) {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await quotechJson(`/api/emails/${emailId}/sender-teammate-override/`, {
        method: 'POST',
        body: role ? { action, role } : { action },
      })
      const externalised =
        action === 'not_teammate' ||
        (action === 'set_role' && role != null && !SENDER_ROLES[role].internal)
      setTeammateOverridden(externalised)
      setChosenRole(action === 'set_role' && role ? role : null)
      setRoleMenuOpen(false)
      onProfileCreated?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update')
    } finally {
      setBusy(false)
    }
  }

  async function handleAddToCrm() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const { data } = await quotechJson<CreateProfileResponse>(
        `/api/emails/${emailId}/create-sender-profile/`,
        { method: 'POST', body: {} },
      )
      if (data) setCreated(data)
      onProfileCreated?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add to CRM')
    } finally {
      setBusy(false)
    }
  }

  // "Wrong person? Fix" (BUG-2026-07-09-INTERNAL-SENDER-CRM): the CRM
  // matched the WRONG Contact for this address (a stale row carrying
  // someone else's email). Severs the address off the offending
  // Contact row(s) server-side — the Contact survives, only the bad
  // address link is cleared, so the fix is reversible by re-filing.
  async function handleDetachWrongPerson() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await quotechJson(`/api/emails/${emailId}/detach-sender-contact/`, {
        method: 'POST',
        body: {},
      })
      setDetachedWrongPerson(true)
      setCreated(null)
      onProfileCreated?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not fix the match')
    } finally {
      setBusy(false)
    }
  }

  // AP-side counterpart of "Add to CRM": file the sender into the supplier
  // master (quotebuilder.Supplier) — NOT the customer CRM. Operator
  // (2026-06-02): *"I should add that to not the CRM but Supplier… so we
  // create a supplier named that"*. ``kind`` files it as a one-off
  // 'supplier' or a recurring 'subscription' (SaaS/membership/licence
  // overhead). Idempotent on the server (created=false echoes the existing
  // supplier; a 'subscription' click UPGRADES a known supplier in place), so
  // a repeat click still flips the strip to the saved state.
  async function handleAddVendor(kind: VendorKind) {
    if (busy) return
    setBusy(true)
    setPendingKind(kind)
    setError(null)
    try {
      const { data } = await quotechJson<CreateSupplierResponse>(
        `/api/emails/${emailId}/create-sender-supplier/`,
        { method: 'POST', body: { kind } },
      )
      if (data) setSupplierSaved(data)
      onProfileCreated?.()
    } catch (err) {
      const verb = kind === 'subscription' ? 'subscription' : 'supplier'
      setError(err instanceof Error ? err.message : `Could not add ${verb}`)
    } finally {
      setBusy(false)
      setPendingKind(null)
    }
  }

  return {
    busy,
    error,
    pendingKind,
    created,
    supplierSaved,
    detachedWrongPerson,
    teammateOverridden,
    chosenRole,
    roleMenuOpen,
    setRoleMenuOpen,
    addToCrm: () => void handleAddToCrm(),
    addVendor: (kind) => void handleAddVendor(kind),
    detachWrongPerson: () => void handleDetachWrongPerson(),
    overrideTeammate: (action, role) => void handleTeammateOverride(action, role),
  }
}
